import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { type AnalyticsBiCatalogView, type AnalyticsBiQueryView } from '@vello/api-client';
import { type ResultRow } from '@vello/analytics-semantic';
import { Badge, BarList, Breadcrumb, Button, CheckRow, EmptyState, Field, FilterBar, FormGrid, FormRow, ListTable, Notice, PageHeader, Panel, SectionTitle, SegmentedControl, Select, TextInput, type Column, Stack, Text, Row, InlineRow, Grid, InlineStack } from '@vello/web-theme';
import {
  GRAINS,
  PAGE_SIZES,
  RANKING_SIZES,
  type BiFilterState,
  type BiUnit,
  type CrossFilterSelect,
  type ExplorerState,
  type ExplorerUrlState,
  type TrailStep,
  applyCrossFilter,
  filterOffers,
  biErrorView,
  computeDelta,
  defaultExplorerState,
  type DrillChoice,
  type Offer,
  deltaTone,
  dimensionInfo,
  dimensionOffers,
  drillChoices,
  drillState,
  enabledIds,
  expectedMarkets,
  explorerIssue,
  exportRequest,
  metricOffers,
  rankingState,
  reportQuery,
  sanitizeState,
  hasRegionDimension,
  scopeFilters,
  sharedBiQueue,
  trailBack,
} from '../../src/index';
import { FormDialog } from '../components/form-dialog';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { AnswerNotes, BiProblem, CompareControls, FreshnessBadge, PeriodControls, useBiText, type BiText } from '../components/bi/bi-ui';
import { ExplorerChart } from '../components/bi/ExplorerChart';
import { FilterBuilder, type FilterRefusal } from '../components/bi/FilterBuilder';
import { FilterSetsMenu } from '../components/bi/FilterSetsMenu';
import { ShareLinkButton, UrlNotices, useUrlSync } from '../components/bi/ShareLink';
import { useBiAccess, useBiCatalog, useExplorerData } from '../components/bi/bi-hooks';
import { takePendingReport, useSavedReports } from '../components/bi/bi-reports-store';
import { downloadText } from '../components/bi/download';
import { ExportDialog, type ExportTarget } from '../components/bi/ExportDialog';

/**
 * Análises › Explorador (D522; doc 20 §4.3): pick measures, cuts, filters, a period and a comparison; read the result as a table
 * (sorted, paged) and a chart; click a row to open it one level deeper (year → month → day → hour, country → market, merchant →
 * location, category → item, a person → the orders) and come back through the trail; or ask for a ranking (the top or bottom N of
 * any measure). Everything is a new server request — nothing is filtered or cached on the client. A person’s name, and the
 * person/order grain, are offered only with `analytics:view_personal`; the CSV only with `analytics:export`.
 */
export function AnalyticsExplorerScreen(): ReactElement {
  const { t, lang } = useT();
  const { scope, runtime, markets } = useAdmin();
  const { toast } = useConsole();
  const { state: catalogState, reload: reloadCatalog } = useBiCatalog();
  const catalog = catalogState.status === 'ready' ? catalogState.value : undefined;
  const access = useBiAccess(catalog);
  const text = useBiText(catalog);
  const reports = useSavedReports();

  const [draft, setDraft] = useState<ExplorerState>(defaultExplorerState);
  const [applied, setApplied] = useState<ExplorerState | undefined>(undefined);
  const [trail, setTrail] = useState<readonly TrailStep[]>([]);
  const [saveName, setSaveName] = useState('');
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  /** A row with more than one way down: the operator picks which (D523). */
  const [drillPick, setDrillPick] = useState<{ readonly row: ResultRow; readonly view: AnalyticsBiQueryView; readonly choices: readonly DrillChoice[] } | undefined>(undefined);
  const [exportProblem, setExportProblem] = useState<string | undefined>(undefined);
  const [exporting, setExporting] = useState(false);
  /** “Run query” on an unchanged query asks the server again (the data moves). */
  const [nonce, setNonce] = useState(0);
  const started = useRef(false);

  const scopeKey = JSON.stringify(scope.current);
  const regionDimension = hasRegionDimension(catalog);
  const filters = useMemo(() => scopeFilters(scope.current, markets, regionDimension), [scopeKey, markets, regionDimension]); // eslint-disable-line react-hooks/exhaustive-deps -- `scopeKey` is the scope

  // First load: a report handed over by "Relatórios salvos", else the default query.
  useEffect(() => {
    if (catalog === undefined || started.current) return;
    started.current = true;
    const handed = takePendingReport();
    const first = sanitizeState(handed ?? defaultExplorerState(), catalog);
    setDraft(first);
    setApplied(first);
  }, [catalog, access]);

  const { state, reload } = useExplorerData(applied, filters, nonce);

  // D528: the link - the applied question in the address bar, restored (after the server has checked it) when a link is opened.
  const urlState = useMemo<ExplorerUrlState | undefined>(() => (applied === undefined ? undefined : { kind: 'explorer', explorer: applied }), [applied]);
  const sync = useUrlSync<ExplorerUrlState>({
    ready: catalog !== undefined && applied !== undefined,
    current: urlState,
    kind: 'explorer',
    onRestore: (restored) => {
      if (catalog === undefined) return;
      const next = sanitizeState(restored.explorer, catalog);
      setTrail([]);
      setDraft(next);
      setApplied(next);
    },
  });
  const [crossNote, setCrossNote] = useState<string | undefined>(undefined);
  /** A click on a mark of the chart filters the question to its value (Shift adds): a new server request, nothing filtered on the client. */
  const onCrossFilter: CrossFilterSelect = (dimension, value, options) => {
    if (applied === undefined) return;
    const r = applyCrossFilter({ filters: applied.filters, anyOf: applied.anyOf, having: applied.having }, dimension, value, options);
    if (!r.changed) {
      setCrossNote(t(r.reason === 'personal' ? 'bi.flt.cross.personal' : 'bi.flt.cross.notApplied'));
      return;
    }
    setCrossNote(undefined);
    setTrail([]);
    apply({ ...applied, filters: r.state.filters, page: 0 });
  };
  const selectedBuilder: BiFilterState | undefined = applied === undefined ? undefined : { filters: applied.filters, anyOf: applied.anyOf, having: applied.having };
  const answer = state?.status === 'ready' ? state.value : undefined;
  const issue = explorerIssue(draft);
  const expected = catalog !== undefined ? expectedMarkets(scope.current, catalog.markets, markets) : [];

  const apply = (next: ExplorerState): void => {
    setDraft(next);
    setApplied(next);
  };
  const run = (): void => {
    if (issue !== undefined) return;
    setTrail([]);
    setApplied({ ...draft, page: 0 });
    setNonce((n) => n + 1);
  };

  const setMetrics = (metrics: readonly string[]): void => {
    if (catalog === undefined) return;
    const ok = enabledIds(dimensionOffers(catalog, metrics));
    const { sortBy, ...rest } = draft;
    const keepSort = sortBy !== undefined && (metrics.includes(sortBy) || draft.dimensions.includes(sortBy));
    setDraft({
      ...rest,
      ...(keepSort ? { sortBy } : {}),
      metrics,
      dimensions: draft.dimensions.filter((d) => ok.has(d)),
      filters: draft.filters.filter((f) => ok.has(f.dimension) || f.dimension === 'market' || f.dimension === 'country' || f.dimension === 'region' || catalog.dimensions.find((d) => d.id === f.dimension)?.filterOnly === true),
    });
  };

  const onDrill = (row: ResultRow, view: AnalyticsBiQueryView): void => {
    if (applied === undefined || catalog === undefined) return;
    const choices = drillChoices(catalog, view, row);
    if (choices.length === 0) return;
    if (choices.length === 1) drillInto(choices[0] as DrillChoice, row, view);
    else setDrillPick({ row, view, choices });
  };
  const drillInto = (choice: DrillChoice, row: ResultRow, view: AnalyticsBiQueryView): void => {
    if (applied === undefined) return;
    setDrillPick(undefined);
    const next = drillState(applied, view, choice.target);
    if (!('state' in next)) return;
    const key = row.keys[choice.target.dimension];
    setTrail([...trail, { label: text.valueText(choice.target.dimension, key?.key, key?.label), state: applied }]);
    apply(next.state);
  };
  const onTrail = (index: number): void => {
    const back = trailBack(trail, index);
    if (back === undefined) return;
    setTrail(back.trail);
    apply(back.state);
  };

  // D532: the same question as an export in the background (no 50 000-row limit, columns and format of your choice, schedulable).
  const [background, setBackground] = useState<ExportTarget | undefined>(undefined);
  const onBackgroundExport = (): void => {
    if (applied === undefined || answer === undefined) return;
    setBackground({ source: { kind: 'QUERY', query: exportRequest(applied, filters, { fallback: answer.fallback }) } });
  };

  const onExport = async (): Promise<void> => {
    if (applied === undefined || answer === undefined) return;
    setExportProblem(undefined);
    setExporting(true);
    const request = exportRequest(applied, filters, { fallback: answer.fallback });
    const res = await sharedBiQueue.run(() => runtime.analyticsBi.exportCsv(request, { delimiter: lang === 'en' ? ',' : ';' }));
    setExporting(false);
    if (res === undefined) return;
    if (!res.ok) {
      setExportProblem(t(biErrorView(res.error).messageId));
      return;
    }
    const name = `analytics-${answer.view.period.from}_${answer.view.period.to}.csv`;
    if (downloadText(name, res.value)) toast({ tone: 'success', message: t('bi.export.done', { name }) });
    else setExportProblem(t('bi.export.blocked'));
  };

  const onSave = async (): Promise<void> => {
    setSaving(true);
    const res = await reports.add(saveName, reportQuery(applied ?? draft, { fallback: answer?.fallback ?? false }));
    setSaving(false);
    if (!res.ok) {
      setSaveError(t(res.error.messageId));
      return;
    }
    setSaveError(undefined);
    setSaveName('');
    toast({ tone: 'success', message: t('bi.reports.saved', { name: res.value.name }) });
  };

  return (
    <>
      <PageHeader
        title={t('bi.explorer.title')}
        subtitle={t('bi.explorer.subtitle')}
        actions={
          <>
            <FreshnessBadge freshness={answer?.view.freshness} />
            {access.canExport && (
              <Button variant="secondary" icon="download" loading={exporting} disabled={answer === undefined || answer.view.rows.length === 0} onClick={() => void onExport()}>
                {t('bi.export')}
              </Button>
            )}
            {access.canExport && (
              <Button variant="secondary" icon="download" disabled={answer === undefined || answer.view.rows.length === 0} onClick={onBackgroundExport}>
                {t('bi.exportBackground')}
              </Button>
            )}
          </>
        }
      />
      <ExportDialog open={background !== undefined} target={background} onClose={() => setBackground(undefined)} />
      {exportProblem !== undefined && (
        <div>
          <Notice tone="warning">{exportProblem}</Notice>
        </div>
      )}

      {catalogState.status === 'error' && (
        <Panel>
          <BiProblem error={catalogState.error} onRetry={reloadCatalog} text={text} />
        </Panel>
      )}
      {catalogState.status === 'loading' && (
        <Panel>
          <BarList items={[]} loading loadingLabel={t('ui.loading')} />
        </Panel>
      )}

      {catalog !== undefined && (
        <>
          <UrlNotices sync={sync} text={text} />
          <Panel>
            <QueryForm draft={draft} setDraft={setDraft} setMetrics={setMetrics} catalog={catalog} text={text} onRun={run} issue={issue} />
          </Panel>
          {crossNote !== undefined && (
            <div>
              <Notice tone="warning">{crossNote}</Notice>
            </div>
          )}

          {trail.length > 0 && (
            <div>
              <Breadcrumb
                ariaLabel={t('bi.trail')}
                items={[
                  { label: t('bi.trail.root'), onClick: () => onTrail(0) },
                  ...trail.map((step, i) => ({ label: step.label, ...(i + 1 < trail.length ? { onClick: () => onTrail(i + 1) } : {}) })),
                ]}
              />
            </div>
          )}

          <Results
            onCrossFilter={onCrossFilter}
            selected={selectedBuilder}
            applied={applied}
            state={state}
            reload={reload}
            text={text}
            catalog={catalog}
            expected={expected}
            onSort={(sortBy, sortDir) => applied !== undefined && apply({ ...applied, ...(sortBy !== undefined ? { sortBy } : {}), sortDir, page: 0 })}
            onPage={(page) => applied !== undefined && apply({ ...applied, page })}
            onSize={(size) => applied !== undefined && apply({ ...applied, pageSize: size, page: 0 })}
            onDrill={onDrill}
          />

          <Panel>
            <Stack gap={4}>
              <FilterSetsMenu
                idPrefix="bi-ex"
                catalog={catalog}
                text={text}
                current={{ filters: draft.filters, anyOf: draft.anyOf, having: draft.having }}
                allowHaving
                onApply={(state) => {
                  const next = { ...draft, filters: state.filters, anyOf: state.anyOf, having: state.having.filter((h) => draft.metrics.includes(h.metric)), page: 0 };
                  setTrail([]);
                  apply(next);
                }}
              />
              <ShareLinkButton state={urlState} text={text} />
            </Stack>
          </Panel>

          {answer !== undefined && applied !== undefined && reports.canSave && (
            <Panel>
              <SectionTitle level={3} size="sub">{t('bi.reports.save')}</SectionTitle>
              <FormRow
                columns="minmax(0, 28rem)"
                action={
                  <Button variant="secondary" loading={saving} onClick={() => void onSave()}>
                    {t('bi.reports.saveAction')}
                  </Button>
                }
              >
                <Field label={t('bi.reports.name')} htmlFor="bi-save-name" {...(saveError !== undefined ? { error: saveError } : {})} hint={t('bi.reports.hint')}>
                  <TextInput id="bi-save-name" value={saveName} maxLength={80} onChange={(e) => setSaveName(e.target.value)} />
                </Field>
              </FormRow>
            </Panel>
          )}

          <FormDialog
            open={drillPick !== undefined}
            id="bi-drill-by"
            icon="search"
            title={t('bi.drill.by.title')}
            body={t('bi.drill.by.body')}
            busy={false}
            initialFocusId="bi-drill-by-0"
            onCancel={() => setDrillPick(undefined)}
          >
            <Stack gap={2}>
              {drillPick?.choices.map((c, i) => (
                <Button key={c.target.dimension} id={`bi-drill-by-${i}`} variant="secondary" onClick={() => drillInto(c, drillPick.row, drillPick.view)}>
                  {t('bi.drill.by.option', { cut: text.dimName(c.into), of: text.dimName(c.target.dimension), value: text.valueText(c.target.dimension, c.target.key, drillPick.row.keys[c.target.dimension]?.label) })}
                </Button>
              ))}
              <Button variant="ghost" onClick={() => setDrillPick(undefined)}>
                {t('ui.cancel')}
              </Button>
            </Stack>
          </FormDialog>
        </>
      )}
    </>
  );
}

// ───────────────────────────── the query form ─────────────────────────────

function QueryForm({
  draft,
  setDraft,
  setMetrics,
  catalog,
  text,
  onRun,
  issue,
}: {
  draft: ExplorerState;
  setDraft: (s: ExplorerState) => void;
  setMetrics: (m: readonly string[]) => void;
  catalog: AnalyticsBiCatalogView;
  text: BiText;
  onRun: () => void;
  issue: ReturnType<typeof explorerIssue>;
}): ReactElement {
  const { t, v } = text;
  // What the SERVER says this caller may pick beside what is chosen (`catalog.metrics[].refused`): a refused choice is shown, disabled,
  // with its reason written next to it; the ranking selects list only what can be picked.
  const dimOffers = dimensionOffers(catalog, draft.metrics);
  const metricChoices = metricOffers(catalog, draft.metrics, [...draft.dimensions, ...(draft.grain !== 'total' ? [draft.grain] : [])]);
  const dims = dimOffers.filter((o) => o.refusal === undefined).map((o) => dimensionInfo(catalog, o.id));
  const metrics = metricChoices.filter((o) => o.refusal === undefined).map((o) => catalog.metrics.find((m) => m.id === o.id)).filter((m): m is AnalyticsBiCatalogView['metrics'][number] => m !== undefined);
  const ranking = draft.mode === 'ranking';
  // What the SERVER says a condition may be about beside the chosen measures (a person without the word, a table the measures lack).
  const offers = filterOffers(catalog, draft.metrics);
  const refuseFor = (id: string): FilterRefusal | undefined => {
    const r = offers.find((o) => o.id === id)?.refusal;
    return r === undefined ? undefined : { code: r.code, ...(r.because !== undefined ? { because: r.because } : {}) };
  };
  const toggle = (list: readonly string[], id: string, max: number): readonly string[] => (list.includes(id) ? list.filter((x) => x !== id) : list.length >= max ? list : [...list, id]);

  return (
    <Stack gap={5}>
      <SegmentedControl<'table' | 'ranking'>
        ariaLabel={t('bi.mode')}
        value={draft.mode}
        onChange={(mode) => {
          if (mode === 'table') setDraft({ ...draft, mode, pageSize: 25, page: 0 });
          else setDraft(rankingState(draft, { metric: draft.metrics[0] ?? 'orders', dimension: draft.dimensions[0] ?? dims[0]?.id ?? 'market', dir: 'desc', size: 10 }));
        }}
        options={[
          { id: 'table', label: t('bi.mode.table') },
          { id: 'ranking', label: t('bi.mode.ranking') },
        ]}
      />

      {ranking ? (
        <FormGrid min="md">
          <Field label={t('bi.rank.metric')} htmlFor="bi-rank-metric">
            <Select id="bi-rank-metric" value={draft.metrics[0] ?? ''} onChange={(e) => setDraft(rankingState(draft, { metric: e.target.value, dimension: draft.dimensions[0] ?? 'market', dir: draft.sortDir, size: draft.pageSize }))}>
              {metrics.map((m) => (
                <option key={m.id} value={m.id}>
                  {text.metricName(m.id)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('bi.rank.dimension')} htmlFor="bi-rank-dimension">
            <Select id="bi-rank-dimension" value={draft.dimensions[0] ?? ''} onChange={(e) => setDraft(rankingState(draft, { metric: draft.metrics[0] ?? 'orders', dimension: e.target.value, dir: draft.sortDir, size: draft.pageSize }))}>
              {dims.map((d) => (
                <option key={d.id} value={d.id}>
                  {text.dimName(d.id)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('bi.rank.order')} htmlFor="bi-rank-order">
            <Select id="bi-rank-order" value={draft.sortDir} onChange={(e) => setDraft({ ...draft, sortDir: e.target.value as 'asc' | 'desc' })}>
              <option value="desc">{t('bi.rank.top')}</option>
              <option value="asc">{t('bi.rank.bottom')}</option>
            </Select>
          </Field>
          <Field label={t('bi.rank.size')} htmlFor="bi-rank-size">
            <Select id="bi-rank-size" value={String(draft.pageSize)} onChange={(e) => setDraft({ ...draft, pageSize: Number(e.target.value) })}>
              {RANKING_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
          </Field>
        </FormGrid>
      ) : (
        <>
          <Stack as="fieldset" gap={1}>
            <Text as="legend" role="caption" tone="secondary">{t('bi.pick.metrics')}</Text>
            <Grid min="sm" gap={1}>
              {metricChoices.map((o) => (
                <ChoiceRow key={o.id} id={`bi-metric-${o.id}`} offer={o} checked={draft.metrics.includes(o.id)} onChange={() => setMetrics(toggle(draft.metrics, o.id, 12))} label={text.metricName(o.id)} title={text.metricHint(o.id)} text={text} />
              ))}
            </Grid>
          </Stack>
          <Stack as="fieldset" gap={1}>
            <Text as="legend" role="caption" tone="secondary">{t('bi.pick.dimensions')}</Text>
            <Grid min="sm" gap={1}>
              {dimOffers.map((o) => (
                <ChoiceRow
                  key={o.id}
                  id={`bi-dim-${o.id}`}
                  offer={o}
                  checked={draft.dimensions.includes(o.id)}
                  onChange={() => setDraft({ ...draft, dimensions: toggle(draft.dimensions, o.id, 4) })}
                  label={`${text.dimName(o.id)}${dimensionInfo(catalog, o.id).personal ? ` · ${t('bi.personal.tag')}` : ''}`}
                  text={text}
                />
              ))}
            </Grid>
          </Stack>
          <FormGrid min="md">
            <Field label={t('bi.grain')} htmlFor="bi-grain">
              <Select id="bi-grain" value={draft.grain} onChange={(e) => setDraft({ ...draft, grain: e.target.value as ExplorerState['grain'] })}>
                {GRAINS.map((g) => (
                  <option key={g} value={g}>
                    {v('analytics.granularity', g)}
                  </option>
                ))}
              </Select>
            </Field>
          </FormGrid>
          <FilterBuilder
            idPrefix="bi-ex"
            catalog={catalog}
            text={text}
            value={{ filters: draft.filters, anyOf: draft.anyOf, having: draft.having }}
            onChange={(next) => setDraft({ ...draft, filters: next.filters, anyOf: next.anyOf, having: next.having })}
            havingMetrics={draft.metrics}
            refuse={refuseFor}
            dimensionIds={offers.map((o) => o.id)}
          />
        </>
      )}

      <Row gap={5} align="end" wrap>
        <PeriodControls period={draft.period} onChange={(period) => setDraft({ ...draft, period })} idPrefix="bi-ex" />
        <CompareControls compare={draft.compare} upToNowAgainst={draft.upToNowAgainst} onChange={(c) => setDraft({ ...draft, ...c })} idPrefix="bi-ex" />
      </Row>

      {issue !== undefined && <Notice tone="warning">{t(`bi.issue.${issue}`)}</Notice>}
      <div>
        <Button variant="primary" icon="search" disabled={issue !== undefined} onClick={onRun}>
          {t('bi.run')}
        </Button>
      </div>
    </Stack>
  );
}

/** Why a choice is disabled, in words: the server's reason code, and the choice that causes it. */
function refusalText(offer: Offer, text: BiText): string | undefined {
  const refusal = offer.refusal;
  if (refusal === undefined) return undefined;
  const because = refusal.because ?? '';
  switch (refusal.code) {
    case 'ANALYTICS_PERSONAL_FORBIDDEN':
      return text.t('bi.refused.personal');
    case 'ANALYTICS_LINKAGE_FORBIDDEN':
      return text.t('bi.refused.linkage');
    case 'METRICS_SPAN_FACT_TABLES':
      return text.t('bi.refused.span', { other: text.metricName(because) });
    case 'DIMENSION_NOT_AVAILABLE_FOR_METRICS':
      return text.t('bi.refused.unavailableDim', { other: text.metricName(because) });
  }
}

/**
 * A check row that may be refused up front: disabled, the reason named to assistive technology and shown as a tooltip.
 * The reason is NOT printed under the row (D854, L7a-031): a 2-line note on some rows made the grid ragged and hard to scan.
 */
function ChoiceRow({ id, offer, checked, onChange, label, title, text }: { id: string; offer: Offer; checked: boolean; onChange: () => void; label: string; title?: string; text: BiText }): ReactElement {
  const why = refusalText(offer, text);
  const tooltip = why ?? (title !== undefined && title !== '' ? title : undefined);
  return (
    <InlineStack {...(tooltip !== undefined ? { title: tooltip } : {})}>
      <CheckRow id={id} checked={checked && why === undefined} onChange={onChange} {...(why !== undefined ? { disabled: true, describedBy: `${id}-why` } : {})}>
        {label}
      </CheckRow>
      {why !== undefined && (
        <span id={`${id}-why`} className="vello-sr-only">
          {why}
        </span>
      )}
    </InlineStack>
  );
}

// ───────────────────────────── results ─────────────────────────────

function Results({
  applied,
  state,
  reload,
  text,
  catalog,
  expected,
  onSort,
  onPage,
  onSize,
  onDrill,
  onCrossFilter,
  selected,
}: {
  onCrossFilter: CrossFilterSelect;
  selected: BiFilterState | undefined;
  applied: ExplorerState | undefined;
  state: ReturnType<typeof useExplorerData>['state'];
  reload: () => void;
  text: BiText;
  catalog: AnalyticsBiCatalogView;
  expected: readonly string[];
  onSort: (sortBy: string | undefined, dir: 'asc' | 'desc') => void;
  onPage: (page: number) => void;
  onSize: (size: number) => void;
  onDrill: (row: ResultRow, view: AnalyticsBiQueryView) => void;
}): ReactElement {
  const { t, fmt } = text;
  const answer = state?.status === 'ready' ? state.value : undefined;
  const view = answer?.view;
  const loading = state === undefined || state.status === 'loading';
  const error = state?.status === 'error' ? state.error : undefined;

  const columns: readonly Column<ResultRow>[] = useMemo(() => {
    if (view === undefined) return [];
    const cols: Column<ResultRow>[] = [];
    for (const d of view.dimensions) {
      cols.push({
        key: `d:${d.id}`,
        header: text.dimName(d.id),
        render: (r) => <span>{text.valueText(d.id, r.keys[d.id]?.key, r.keys[d.id]?.label)}</span>,
      });
    }
    if (view.rows.some((r) => r.currency !== undefined)) cols.push({ key: 'currency', header: text.dimName('currency'), width: 'max-content', render: (r) => <span className="tnum">{r.currency ?? ''}</span> });
    for (const m of view.metrics) {
      const unit = m.unit as BiUnit;
      cols.push({
        key: `m:${m.id}`,
        header: text.metricName(m.id),
        numeric: true,
        render: (r) => {
          const shown = fmt.cell(unit, r.values[m.id], r.currency);
          const base = r.baseline?.[m.id];
          const d = base !== undefined ? computeDelta(unit, r.values[m.id], base) : undefined;
          const written = d !== undefined ? fmt.delta(d, t('bi.pp')) : undefined;
          return (
            <InlineRow gap={2} align="center" justify="end" wrap>
              <span className="tnum">{shown ?? t('ui.unavailable')}</span>
              {d !== undefined && written !== undefined && <Badge tone={deltaTone(m.id, d)}>{written}</Badge>}
            </InlineRow>
          );
        },
      });
    }
    return cols;
  }, [view, text, fmt, t]);

  const drillable = view !== undefined && view.rows.length > 0 && view.rows.some((r) => drillChoices(catalog, view, r).length > 0);
  const sortOptions = view !== undefined ? [...view.metrics.map((m) => ({ id: m.id, label: text.metricName(m.id) })), ...view.dimensions.map((d) => ({ id: d.id, label: text.dimName(d.id) }))] : [];
  const offset = (applied?.page ?? 0) * (applied?.pageSize ?? 0);
  const ranking = applied?.mode === 'ranking';
  const hasPeople = view?.dimensions.some((d) => d.personal) ?? false;
  const multiCurrency = view !== undefined && new Set(view.rows.map((r) => r.currency).filter((c) => c !== undefined)).size > 1;

  return (
    <Stack gap={4}>
      {error !== undefined && <BiProblem error={error} onRetry={reload} text={text} />}
      {view !== undefined && <AnswerNotes view={view} fallback={answer?.fallback ?? false} expectedMarkets={expected} text={text} />}
      {hasPeople && <Notice tone="neutral">{t('bi.personal.notice')}</Notice>}
      {multiCurrency && <Notice tone="neutral">{t('bi.currency.note')}</Notice>}

      {/* D528: a click on a mark of ANY chart type reaches the filter through the one contract (`CrossFilterSelect`); a person is never filtered. */}
      {view !== undefined && view.rows.length > 0 && (
        <ExplorerChart
          view={view}
          text={text}
          onSelect={(s) => {
            // The chart reports the mark it drew (often its label); the filter needs the dimension's own key.
            const key = markKey(view, text, s.dimension, s.value);
            if (key !== undefined) onCrossFilter(s.dimension, key);
          }}
          selected={selectedByDimension(selected)}
        />
      )}

      {applied !== undefined && view !== undefined && view.rows.length > 0 && !ranking && (
        <FilterBar>
          <Field label={t('bi.sort')} htmlFor="bi-sort">
            <Select id="bi-sort" value={applied.sortBy ?? view.metrics[0]?.id ?? ''} onChange={(e) => onSort(e.target.value, applied.sortDir)}>
              {sortOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
          <SegmentedControl<'asc' | 'desc'>
            ariaLabel={t('bi.sort.direction')}
            value={applied.sortDir}
            onChange={(dir) => onSort(applied.sortBy, dir)}
            options={[
              { id: 'desc', label: t('bi.sort.desc') },
              { id: 'asc', label: t('bi.sort.asc') },
            ]}
          />
          <Field label={t('bi.page.size')} htmlFor="bi-page-size">
            <Select id="bi-page-size" value={String(applied.pageSize)} onChange={(e) => onSize(Number(e.target.value))}>
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
          </Field>
        </FilterBar>
      )}
      {drillable && <Text as="p" role="caption" tone="secondary">{t('bi.drill.hint')}</Text>}

      <ListTable
        columns={columns}
        rows={view?.rows ?? []}
        rowKey={(r) => JSON.stringify([r.keys, r.marketId ?? '', r.currency ?? ''])}
        loading={loading}
        loadingLabel={t('ui.loading')}
        staggerKey={view?.requestId}
        empty={error === undefined ? <EmptyState title={t('bi.empty')} detail={t('bi.empty.hint')} /> : undefined}
        {...(drillable && view !== undefined ? { onRowClick: (r: ResultRow) => onDrill(r, view) } : {})}
        {...(!ranking && view !== undefined && applied !== undefined && (applied.page > 0 || view.hasMore)
          ? {
              pagination: {
                from: offset + 1,
                to: offset + view.rows.length,
                total: view.total ?? (view.hasMore ? offset + view.rows.length + 1 : offset + view.rows.length),
                hasPrev: applied.page > 0,
                hasNext: view.hasMore,
                onPrev: () => onPage(Math.max(0, applied.page - 1)),
                onNext: () => onPage(applied.page + 1),
                prevLabel: t('bi.page.prev'),
                nextLabel: t('bi.page.next'),
                // "11 to 20 of 63" when the server counted the whole answer; without the total (it did not fit in the time budget) "11 to 20".
                rangeLabel:
                  view.total !== null
                    ? t('bi.page.rangeOf', { from: fmt.count(offset + 1), to: fmt.count(offset + view.rows.length), total: fmt.count(view.total) })
                    : t('bi.page.range', { from: fmt.count(offset + 1), to: fmt.count(offset + view.rows.length) }),
              },
            }
          : {})}
        minWidth={640}
      />
    </Stack>
  );
}

/** The values chosen per dimension (the inclusion conditions of the filter), so the marks of a chart can show which are selected. */
function selectedByDimension(state: BiFilterState | undefined): Record<string, readonly string[]> {
  const out: Record<string, string[]> = {};
  for (const f of state?.filters ?? []) if (f.op === 'in' || f.op === 'eq') out[f.dimension] = [...(out[f.dimension] ?? []), ...f.values];
  return out;
}

/** The key of the row a chart mark stands for: the mark's value is a key already, or the text the chart drew for one of the rows. */
function markKey(view: AnalyticsBiQueryView, text: BiText, dimension: string, value: string): string | undefined {
  const keyOf = (r: ResultRow): string | null | undefined => r.keys[dimension]?.key;
  const byKey = view.rows.find((r) => keyOf(r) === value);
  if (byKey !== undefined) return value;
  const drawn = (r: ResultRow, short: boolean): string => text.valueText(dimension, keyOf(r), r.keys[dimension]?.label, short);
  const row = view.rows.find((r) => drawn(r, false) === value) ?? view.rows.find((r) => drawn(r, true) === value);
  const key = row === undefined ? undefined : keyOf(row);
  return key === null ? undefined : key;
}
